import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, Loader2, PackageSearch } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// FLD-1 — the foreman's Request material. Runs the Not-On-Hand check before
// anything is written, then creates {job}-PT-{seq} with the short lines flagged.
export default function PickingRequestDialog({ projectId, selection = [], needBy = "", actor = "",
                                               uncoveredNote = "", onClose, onCreated }) {
  const [check, setCheck] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [addTo, setAddTo] = useState("");
  const [date, setDate] = useState(needBy);
  const [who, setWho] = useState(actor);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [dropped, setDropped] = useState({});   // frs_item_key -> true: leave it OFF this ticket

  const kept = useMemo(() => selection.filter((s) => !dropped[s.frs_item_key]), [selection, dropped]);
  const lines = useMemo(() => kept.map((s) => ({ frs_item_key: s.frs_item_key, qty: s.qty })), [kept]);
  const totals = useMemo(() => ({
    rooms: new Set(kept.map((s) => s.room_key)).size,
    products: new Set(kept.map((s) => s.product_uid)).size,
    units: kept.reduce((a, s) => a + (s.qty || 0), 0),
  }), [kept]);

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const [c, t] = await Promise.all([
          api.post("/picking-tickets/request", { project_id: projectId, mode: "check",
            lines: selection.map((s) => ({ frs_item_key: s.frs_item_key, qty: s.qty })) }),
          api.get("/material/tickets", { params: { project_id: projectId, status: "Requested" } }),
        ]);
        setCheck(c.data);
        setTickets(t.data || []);
        // system prepares, human confirms: anything already fully covered starts OFF the ticket
        setDropped(Object.fromEntries((c.data.already || []).filter((a) => a.covered)
          .map((a) => [a.frs_item_key, true])));
      } catch (e) {
        toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Could not check on hand");
        onClose?.();
      } finally { setBusy(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const create = async (mode) => {
    if (!lines.length) { toast.error("Everything selected was left off — nothing to request"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/picking-tickets/request", {
        project_id: projectId, mode, lines, need_by: date, requested_by: who, notes,
        add_to_ticket_id: addTo,
      });
      const s = data.summary;
      toast.success(`${data.ticket.ticket_number} — ${s.rooms} room(s), ${s.products} product(s), ${s.units} unit(s)`
        + (s.not_on_hand.length ? ` · ${s.not_on_hand.length} Not On Hand` : "")
        + (s.not_ordered.length ? ` · ${s.not_ordered.length} Not Ordered` : ""));
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not create the pull ticket");
    } finally { setBusy(false); }
  };

  const already = check?.already || [];
  const blocking = (check?.blocking || []).filter((b) => !dropped[b.frs_item_key]);
  const shorts = [...new Set(blocking.map((b) => b.product_short || b.product_uid))];
  // One line per product: 17 rooms wanting GB-5806x18 read as "GB-5806x18 · 18 requested · 17 rooms".
  const blockingByProduct = Object.values(blocking.reduce((acc, b) => {
    const k = b.product_uid || b.product_short;
    const g = acc[k] || (acc[k] = { key: k, product: b.product_short || b.product_uid, requested: 0, rooms: 0,
      on_hand: b.on_hand_at_request || 0, inbound: b.inbound_at_request || 0, line_status: b.line_status, po_ref: b.po_ref });
    g.requested += Number(b.requested) || 0;
    g.rooms += 1;
    if (b.line_status === "Not Ordered") g.line_status = "Not Ordered";
    return acc;
  }, {})).sort((x, y) => String(x.product).localeCompare(String(y.product), undefined, { numeric: true }));
  const droppedCount = already.filter((a) => dropped[a.frs_item_key]).length;
  const toggleDrop = (key) => setDropped((d) => ({ ...d, [key]: !d[key] }));

  return (
    <Dialog open onOpenChange={() => onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="picking-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageSearch className="w-4 h-4 text-blue-700" /> Request material
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500" data-testid="picking-request-summary">
            {totals.rooms} room(s) · {totals.products} product(s) · {totals.units} unit(s)
            {uncoveredNote ? ` — ${uncoveredNote}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 text-sm">
          <div>
            <Label className="text-[10px] uppercase text-gray-500">Need material by</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className="h-8 text-xs" data-testid="picking-request-needby" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-gray-500">Requested by</Label>
            <Input value={who} onChange={(e) => setWho(e.target.value)} className="h-8 text-xs"
                   data-testid="picking-request-by" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-gray-500">Add to open ticket</Label>
            <select value={addTo} onChange={(e) => setAddTo(e.target.value)}
                    data-testid="picking-request-add-to"
                    className="h-8 w-full text-xs border border-gray-300 rounded px-1 bg-white">
              <option value="">— new ticket —</option>
              {tickets.map((t) => (
                <option key={t.id} value={t.id}>{t.ticket_number} · {t.rollup?.lines || 0} line(s)</option>
              ))}
            </select>
          </div>
          <div className="col-span-3">
            <Label className="text-[10px] uppercase text-gray-500">Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-8 text-xs"
                   data-testid="picking-request-notes" />
          </div>
        </div>

        {busy && !check && (
          <div className="text-xs text-gray-500 flex items-center gap-2" data-testid="picking-request-checking">
            <Loader2 className="w-3 h-3 animate-spin" /> Checking on hand and inbound…
          </div>
        )}

        {already.length > 0 && (
          <div className="border border-sky-300 bg-sky-50 rounded p-2 space-y-2 text-xs"
               data-testid="picking-request-already">
            <div className="text-sky-900 flex items-start gap-1">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                <span className="font-semibold">{already.length} item(s)</span> you selected are already on a
                ticket and/or already on site. Is that right? Untick <b>Remove</b> to keep one on this ticket
                anyway.
              </span>
            </div>
            <table className="w-full">
              <thead className="text-[10px] uppercase text-sky-900/70">
                <tr><th className="text-left">Room</th><th className="text-left">Product</th>
                  <th className="text-right">Selected</th><th className="text-right">On ticket</th>
                  <th className="text-right">On site</th><th className="text-left pl-2">Ticket(s)</th>
                  <th className="text-right">Remove</th></tr>
              </thead>
              <tbody>
                {already.map((a) => (
                  <tr key={a.frs_item_key} data-testid={`picking-already-${a.frs_item_key}`}
                      className={dropped[a.frs_item_key] ? "text-gray-400 line-through" : ""}>
                    <td>{a.room}</td>
                    <td>{a.product_short}</td>
                    <td className="text-right tabular-nums">{a.requested}</td>
                    <td className="text-right tabular-nums">{a.on_ticket || "—"}</td>
                    <td className="text-right tabular-nums">{a.onsite || "—"}</td>
                    <td className="pl-2 text-[11px]">{a.tickets.join(", ")}</td>
                    <td className="text-right">
                      <input type="checkbox" checked={!!dropped[a.frs_item_key]}
                             onChange={() => toggleDrop(a.frs_item_key)}
                             data-testid={`picking-already-remove-${a.frs_item_key}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center gap-2 text-sky-900">
              <span data-testid="picking-request-dropped">{droppedCount} of {already.length} left off this ticket</span>
              <button type="button" className="underline" data-testid="picking-already-remove-all"
                      onClick={() => setDropped(Object.fromEntries(already.map((a) => [a.frs_item_key, true])))}>
                Remove all
              </button>
              <button type="button" className="underline" data-testid="picking-already-keep-all"
                      onClick={() => setDropped({})}>Keep all</button>
            </div>
          </div>
        )}

        {check && blocking.length === 0 && lines.length > 0 && (
          <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5"
               data-testid="picking-request-all-covered">
            Everything selected is on hand or on order.
          </div>
        )}

        {check && blocking.length > 0 && (
          <div className="border border-amber-300 bg-amber-50 rounded p-2 space-y-2 text-xs"
               data-testid="picking-request-not-on-hand">
            <div className="text-amber-900 flex items-start gap-1">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Items <span className="font-semibold">{shorts.join(", ")}</span> are not on hand
                {blocking.some((b) => b.po_ref)
                  ? ` — ${blocking.filter((b) => b.po_ref).map((b) => `${b.po_ref} expected ${b.expected_arrival || "TBC"}`).join("; ")}`
                  : ""}. Continue with the pull ticket?
              </span>
            </div>
            <table className="w-full">
              <thead className="text-[10px] uppercase text-amber-900/70">
                <tr><th className="text-left">Product</th><th className="text-right">Requested</th>
                  <th className="text-right">Rooms</th>
                  <th className="text-right">On hand</th><th className="text-right">Inbound</th>
                  <th className="text-left pl-2">Status</th></tr>
              </thead>
              <tbody>
                {blockingByProduct.map((b) => (
                  <tr key={b.key} data-testid={`picking-blocking-${b.key}`}>
                    <td className="font-medium">{b.product}</td>
                    <td className="text-right tabular-nums font-semibold">{b.requested}</td>
                    <td className="text-right tabular-nums text-amber-900/70">{b.rooms}</td>
                    <td className="text-right tabular-nums">{b.on_hand}</td>
                    <td className="text-right tabular-nums">{b.inbound}</td>
                    <td className={`pl-2 font-semibold ${b.line_status === "Not Ordered" ? "text-red-700" : "text-amber-800"}`}>
                      {b.line_status}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {check.not_ordered?.length > 0 && (
              <div className="text-red-800" data-testid="picking-request-not-ordered">
                {check.not_ordered.length} line(s) have no PO at all — they are raised to the PM as Not Ordered.
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => onClose?.()} data-testid="picking-request-hold">
            Hold
          </Button>
          {blocking.length > 0 && (
            <Button variant="outline" disabled={busy} onClick={() => create("remove")}
                    data-testid="picking-request-remove">
              Remove those lines
            </Button>
          )}
          <Button disabled={busy || !check || lines.length === 0} onClick={() => create("continue")}
                  data-testid="picking-request-continue">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
            {blocking.length > 0 ? "Continue" : "Create pull ticket"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
